/**
 * SCOPE-CONFIG-1 Level 1 — operator view of tier membership and pricing.
 *
 * super_admin only (the route is also role-guarded; this is the second lock,
 * not the first). Two rules the UI exists to enforce:
 *
 *   1. A membership change is never silent. Preview first, then confirm with
 *      the resulting counts — the server 409s on anything else, and this page
 *      shows the before/after counts rather than making the operator infer them.
 *   2. The committed catalogue stays the baseline. Everything edited here is an
 *      overlay on top of it, so "Reset" is always available and always exact.
 *
 * Per-deal negotiated scope is Level 2 and lives on the deal, not here.
 */
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  fetchScopeConfig,
  previewScopeConfig,
  saveScopeConfig,
  type ScopeConfig,
  type ScopeConfigResponse,
} from '@/api/scope'
import { PRICING_ENABLED } from '@/lib/pricing'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { formatApiError } from '@/utils/httpErrors'

const EMPTY: ScopeConfig = { membership: {}, pricing: {} }

/** "3, 7, 12" -> [3, 7, 12]. Tolerant of spaces, trailing commas and newlines. */
function parseNumbers(raw: string): number[] {
  return raw
    .split(/[\s,]+/)
    .map(s => s.trim())
    .filter(Boolean)
    .map(Number)
    .filter(n => Number.isInteger(n) && n > 0)
}

function fmtNumbers(list: number[] | undefined): string {
  return (list ?? []).join(', ')
}

export function ScopeConfigPage() {
  const [server, setServer] = useState<ScopeConfigResponse | null>(null)
  const [draft, setDraft] = useState<ScopeConfig>(EMPTY)
  const [preview, setPreview] = useState<ScopeConfigResponse | null>(null)
  const [reason, setReason] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await fetchScopeConfig()
      setServer(data)
      setDraft({ membership: data.config.membership ?? {}, pricing: data.config.pricing ?? {} })
      setPreview(null)
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const tiers = server?.tiers ?? []

  // The counts the operator is about to commit to: the preview when one has
  // been run, otherwise what the server last reported. Never computed here —
  // a client-side count is exactly the drift this feature is meant to prevent.
  const shownCounts = preview?.counts ?? server?.counts ?? {}
  const shownPricing = preview?.pricing ?? server?.pricing ?? {}

  const dirty = useMemo(() => {
    if (!server) return false
    return JSON.stringify(draft) !== JSON.stringify({
      membership: server.config.membership ?? {},
      pricing: server.config.pricing ?? {},
    })
  }, [draft, server])

  function setMembership(tier: string, field: 'added' | 'removed', raw: string) {
    setPreview(null)
    setDraft(d => ({
      ...d,
      membership: {
        ...d.membership,
        [tier]: { ...(d.membership?.[tier] ?? {}), [field]: parseNumbers(raw) },
      },
    }))
  }

  function setPricing(tier: string, field: 'base' | 'per_million', raw: string) {
    setPreview(null)
    const value = raw.trim() === '' ? undefined : Number(raw)
    setDraft(d => {
      const next = { ...(d.pricing?.[tier] ?? {}) } as Record<string, number | undefined>
      if (value === undefined || !Number.isFinite(value)) delete next[field]
      else next[field] = value
      return { ...d, pricing: { ...d.pricing, [tier]: next as { base?: number; per_million?: number } } }
    })
  }

  async function runPreview() {
    setBusy(true)
    setError(null)
    try {
      const result = await previewScopeConfig(draft)
      setPreview(result)
      toast.success('Preview computed — nothing has been saved yet.')
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setBusy(false)
    }
  }

  async function save() {
    if (!preview) {
      toast.error('Preview the change first — the confirmation uses its counts.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const saved = await saveScopeConfig(draft, preview.counts, reason.trim() || undefined)
      setServer(saved)
      setPreview(null)
      setReason('')
      toast.success('Scope configuration saved.')
    } catch (err) {
      // A 409 means the counts moved between preview and save. Re-preview
      // rather than retrying blind: the operator must see the new numbers.
      setError(formatApiError(err))
      setPreview(null)
    } finally {
      setBusy(false)
    }
  }

  async function resetToBaseline() {
    setBusy(true)
    setError(null)
    try {
      const cleared: ScopeConfig = { membership: {}, pricing: {} }
      const previewed = await previewScopeConfig(cleared)
      const saved = await saveScopeConfig(cleared, previewed.counts, reason.trim() || 'Reset to committed baseline')
      setServer(saved)
      setDraft(cleared)
      setPreview(null)
      setReason('')
      toast.success('Reset to the committed catalogue baseline.')
    } catch (err) {
      setError(formatApiError(err))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading scope configuration…</div>
  }
  if (!server) {
    return (
      <div className="p-6 space-y-3">
        <p className="text-destructive">{error ?? 'Scope configuration is unavailable.'}</p>
        <Button variant="outline" onClick={() => void load()}>Try again</Button>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl">
      <header>
        <h1 className="text-2xl font-semibold">Scope &amp; tier configuration</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Adjusts which of the {server.catalogue_count} catalogue procedures each service
          level covers, and the fee coefficients used to price it. Changes apply to deals
          scoped from here on; existing deals keep the scope they were sold.
        </p>
      </header>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive whitespace-pre-wrap">
          {error}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Procedure count per service level</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-2 pr-4 font-medium">Service level</th>
                  <th className="py-2 pr-4 font-medium text-right">Committed baseline</th>
                  <th className="py-2 pr-4 font-medium text-right">In effect</th>
                  <th className="py-2 font-medium text-right">
                    {preview ? 'After this change' : ''}
                  </th>
                </tr>
              </thead>
              <tbody>
                {tiers.map(t => {
                  const baseline = server.baseline_counts[t.key]
                  const current = server.counts[t.key]
                  const next = preview?.counts?.[t.key]
                  const moved = preview !== null && next !== current
                  return (
                    <tr key={t.key} className="border-b last:border-0">
                      <td className="py-2 pr-4">{t.label}</td>
                      <td className="py-2 pr-4 text-right tabular-nums text-muted-foreground">
                        {baseline}
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">{current}</td>
                      <td className={`py-2 text-right tabular-nums ${moved ? 'font-semibold text-primary' : 'text-muted-foreground'}`}>
                        {preview ? next : ''}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {tiers.map(t => (
        <Card key={t.key}>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between gap-3">
              <span>{t.label}</span>
              <span className="text-sm font-normal text-muted-foreground tabular-nums">
                {shownCounts[t.key]} of {server.catalogue_count}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor={`add-${t.key}`}>Add procedure numbers</Label>
                <Input
                  id={`add-${t.key}`}
                  placeholder="e.g. 12, 31"
                  defaultValue={fmtNumbers(draft.membership?.[t.key]?.added)}
                  onChange={e => setMembership(t.key, 'added', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor={`rm-${t.key}`}>Remove procedure numbers</Label>
                <Input
                  id={`rm-${t.key}`}
                  placeholder="e.g. 5"
                  defaultValue={fmtNumbers(draft.membership?.[t.key]?.removed)}
                  onChange={e => setMembership(t.key, 'removed', e.target.value)}
                />
              </div>
            </div>

            {PRICING_ENABLED && (
              <div className="grid gap-4 sm:grid-cols-2 pt-2 border-t">
                <div>
                  <Label htmlFor={`base-${t.key}`}>Base fee (USD)</Label>
                  <Input
                    id={`base-${t.key}`}
                    inputMode="decimal"
                    placeholder={String(server.baseline_pricing[t.key]?.base ?? '')}
                    defaultValue={draft.pricing?.[t.key]?.base ?? ''}
                    onChange={e => setPricing(t.key, 'base', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor={`pm-${t.key}`}>Per $1M EBITDA (USD)</Label>
                  <Input
                    id={`pm-${t.key}`}
                    inputMode="decimal"
                    placeholder={String(server.baseline_pricing[t.key]?.per_million ?? '')}
                    defaultValue={draft.pricing?.[t.key]?.per_million ?? ''}
                    onChange={e => setPricing(t.key, 'per_million', e.target.value)}
                  />
                </div>
                <p className="sm:col-span-2 text-xs text-muted-foreground">
                  In effect: {shownPricing[t.key]?.base} + {shownPricing[t.key]?.per_million} × EBITDA($M)
                  {shownPricing[t.key]?.source === 'config' ? ' (overridden)' : ' (baseline)'}
                  {' · '}committed baseline: {server.baseline_pricing[t.key]?.formula}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Apply</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="reason">Reason (recorded in the audit trail)</Label>
            <Textarea
              id="reason"
              rows={2}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Why this scope or pricing is changing"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => void runPreview()} disabled={busy}>
              Preview
            </Button>
            <Button onClick={() => void save()} disabled={busy || !preview}>
              {busy ? 'Saving…' : 'Save configuration'}
            </Button>
            <Button variant="ghost" onClick={() => void resetToBaseline()} disabled={busy}>
              Reset to baseline
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {preview
              ? 'Preview computed. Saving confirms the counts shown above.'
              : dirty
                ? 'Preview the change to see the resulting counts before saving.'
                : 'No unsaved changes.'}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

export default ScopeConfigPage
